import React, { useState, useRef, useEffect } from "react";
import Image from "../../components/AppImage";
import Icon from "../../components/AppIcon";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import {
  getAllProductsService,
  toggleFavService,
} from "redux/services/general_service";
import Skeleton from "react-loading-skeleton";
import Link from '../../components/ui/Link';
const FeaturedProducts = () => {
  const { t, i18n } = useTranslation();
  const dispatch = useDispatch();
  const [currentIndex, setCurrentIndex] = useState(0);
  const [featuredProducts, setFeaturedProducts] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const scrollContainerRef = useRef(null);

  const itemsPerView = {
    mobile: 2,
    tablet: 3,
    desktop: 4,
  };

  const scroll = (direction) => {
    const container = scrollContainerRef.current;
    if (!container) return;

    const itemWidth = container.children[0]?.offsetWidth || 0;
    const gap = 16; // 1rem gap
    const scrollAmount = itemWidth + gap;

    if (direction === "left") {
      container.scrollBy({ left: -scrollAmount, behavior: "smooth" });
      setCurrentIndex(Math.max(0, currentIndex - 1));
    } else {
      container.scrollBy({ left: scrollAmount, behavior: "smooth" });
      setCurrentIndex(
        Math.min(
          featuredProducts?.length - itemsPerView.mobile,
          currentIndex + 1
        )
      );
    }
  };

  const handleProductClick = (productId) => {
    window.location.href = `/product-detail?id=${productId}`;
  };

  const handleAddToCart = (e, product) => {
    e.stopPropagation();

    const existingCart = JSON.parse(localStorage.getItem("cartItems") || "[]");

    const existingItemIndex = existingCart.findIndex(
      (item) => item.id === product.id
    );

    if (existingItemIndex > -1) {
      existingCart[existingItemIndex].quantity += 1;
    } else {
      existingCart.push({
        id: product.id,
        name: product.name,
        price: product.price,
        image: product.image,
        quantity: 1,
        variant: "Default",
      });
    }

    const newCartCount = existingCart.reduce(
      (total, item) => total + item.quantity,
      0
    );
    localStorage.setItem("cartItems", JSON.stringify(existingCart));
    localStorage.setItem("cartCount", newCartCount.toString());

    window.dispatchEvent(
      new CustomEvent("cartUpdated", {
        detail: { count: newCartCount, items: existingCart },
      })
    );
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat(i18n.language === "en" ? "en" : "ar", {
      style: "currency",
      currency: "IQD",
      maximumFractionDigits: 0,
    }).format(price);
  };

  const renderStars = (rating) => {
    const stars = [];
    const fullStars = Math.floor(rating);
    const hasHalfStar = rating % 1 !== 0;

    for (let i = 0; i < fullStars; i++) {
      stars.push(
        <Icon
          key={i}
          name="Star"
          size={12}
          className="text-yellow-400 fill-current"
        />
      );
    }

    if (hasHalfStar) {
      stars.push(
        <Icon
          key="half"
          name="StarHalf"
          size={12}
          className="text-yellow-400 fill-current"
        />
      );
    }

    const remainingStars = 5 - Math.ceil(rating);
    for (let i = 0; i < remainingStars; i++) {
      stars.push(
        <Icon
          key={`empty-${i}`}
          name="Star"
          size={12}
          className="text-gray-300"
        />
      );
    }

    return stars;
  };

  const onToggleSelection = (e, id) => {
    e.stopPropagation();
    dispatch(toggleFavService({ product_id: id })).then((response) => {
      if (response.payload.status === 200) {
        window.location.reload();
      }
    });
  };

  useEffect(() => {
    setIsLoading(true);
    dispatch(getAllProductsService({ is_special: 1 })).then((response) => {
      if (response?.payload?.status === 200) {
        setFeaturedProducts(response?.payload?.data?.data);
      }
      setIsLoading(false);
    });
  }, [dispatch]);

  return (
      <section className="w-full py-8 lg:py-16">
      <div className="w-full max-w-[1262px] mx-auto px-4 sm:px-6 lg:px-8">

          {/* Section Header */}
                <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-8 lg:mb-10">
                  <h2 className="text-2xl lg:text-4xl font-medium text-black">{t("featuredProducts")}</h2>
                  <Link 
                    href='/product-listing?featured=true' 
                    className="text-xs lg:text-base font-medium text-[#ca0d2d] hover:underline"
                    variant="default"
                    size="medium"
                    target="_self"
                    rel=""
                    onClick={() => {}}
                    aria-label="View all gadgets"
                  >
                      {t('viewAllDeals')}
                  </Link>
                </div>
        <div
          ref={scrollContainerRef}
          className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 lg:gap-5"
          style={{ scrollbarWidth: "none", msOverflowStyle: "none" }}
        >
          {isLoading ? (
            <>
              {Array.from({ length: 5 }).map((_, index) => (
                <div
                  key={index}
                  className="flex-shrink-0 w-48 sm:w-56 lg:w-64 bg-global-6 rounded-[12px] p-[6px] shadow-[0px_4px_28px_#00000014]"
                >
                  <Skeleton height={200} className="rounded-[10px]" />
                  <div className="p-[6px]">
                    <Skeleton height={20} count={3} />
                  </div>
                </div>
              ))}
            </>
          ) : (
            featuredProducts?.slice(0, 8)?.map((product) => (
              <div
                key={product?.id}
                onClick={() => handleProductClick(product?.id)}
                className="bg-global-6 rounded-[12px] p-[4px] sm:p-[6px] lg:p-[8px] shadow-[0px_4px_28px_#00000014] flex flex-col h-full"
              >
                <div className="bg-global-6 rounded-[10px] p-[4px] sm:p-[6px] lg:p-[8px] mb-[4px] sm:mb-[6px] lg:mb-[8px] relative overflow-hidden">
                  {/* صورة المنتج والعناصر العلوية */}
                  <Image
                    src={product?.images[0]?.file_path}
                    alt=""
                    className="w-full h-[132px] sm:h-[198px] lg:h-[264px] object-cover rounded-[10px] group-hover:scale-105 transition-transform duration-300"
                  />
                  {/* خصم */}
                  {product?.price_after_discount !== null && (
                    <div className="absolute top-2 left-2 z-10 text-white text-xs px-2 py-1 rounded-full font-bold">
                    <span className="text-sm font-semibold text-white bg-[#ca0d2d] px-1 py-1 rounded-sm">
                       %{' '}
                      {Math.round(
                        ((product.price - product.price_after_discount) /
                          product.price) *
                          100
                      )}
                     -
                      </span>
                    </div>
                  )}
                  {/* أزرار المفضلة والعين */}
                  <div className="absolute bottom-2 right-2 transition-smooth flex space-x-1">
                    <button
                      onClick={(e) => onToggleSelection(e, product.id)}
                      className="w-8 h-8 bg-white/90 hover:bg-white rounded-full flex items-center justify-center transition-smooth shadow hover:shadow-md"
                      aria-label={
                        product.in_favorite
                          ? t("removeFromFavorites")
                          : t("addToFavorites")
                      }
                      onKeyDown={(e) => e.stopPropagation()}
                    >
                      {product.in_favorite ? (
                        <Icon
                          name="Heart"
                          size={14}
                          className="text-red-500 fill-current"
                        />
                      ) : (
                        <Icon
                          name="Heart"
                          size={14}
                          className="text-gray-500"
                        />
                      )}
                    </button>
                    <button className="w-8 h-8 bg-white/90 hover:bg-white rounded-full flex items-center justify-center transition-smooth shadow hover:shadow-md">
                      <Icon name="Eye" size={14} />
                    </button>
                  </div>
                </div>

                {/* محتوى المنتج (الاسم، السعر، التقييم، ...) */}
                <div className="flex flex-col flex-grow p-[4px] sm:p-[6px] lg:p-[8px]">
                  <h4 className="font-semibold text-global-text2 mb-[2px] line-clamp-2 group-hover:text-primary transition-smooth">
                    {product?.name}
                  </h4>

                  <div className="flex items-center space-x-1 mb-2">
                    <div className="flex space-x-1">
                      {renderStars(product?.rate)}
                    </div>
                    <span className="text-xs text-global-text6">
                      ({product?.reviews})
                    </span>
                  </div>

                  <div className="flex flex-col sm:flex-row sm:items-center sm:space-x-2 mb-3">
                    <span className="text-md font-semibold text-global-text2 font-mono">
                      {product?.price_after_discount !== null
                        ? formatPrice(product?.price_after_discount)
                        : formatPrice(product?.price)}
                    </span>
                    {product?.price_after_discount !== null &&
                      product?.price > product?.price_after_discount && (
                        <span className="text-sm text-global-text6 line-through font-mono mt-1 sm:mt-0">
                          {formatPrice(product?.price)}
                        </span>
                      )}
                  </div>

                  <div className="flex items-center space-x-3 mb-3 text-xs text-global-text6">
                    {product.fastDelivery && (
                      <div className="flex items-center space-x-1">
                        <Icon name="Zap" size={12} className="text-primary" />
                        <span>{t("fastDelivery")}</span>
                      </div>
                    )}
                  </div>
                </div>

                {/* زر أضف إلى السلة في الأسفل */}
                <button
                  //    variant="default"
                    //  size="sm"
                     fullWidth
                      onClick={(e) => handleAddToCart(e, product)}
                    
                      className="text-white bg-[#ca0d2d] px-1 py-1 rounded-sm w-full"
                   
                    >
                      {t('addToCart')}
                    </button>
              </div>
            ))
          )}
        </div>
      </div>
    </section>
  );
};

export default FeaturedProducts;
